import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Toaster } from 'components/ui/sonner';
import Router from 'config/routes/Router';
import { checkFullTokenValidation, getAccessToken, validateTokenWithServer, refreshAccessToken } from 'utils/auth/auth';
import { setSubscriptionCategories } from 'config/redux/slices/userSlice';
import { useTokenRefresh } from 'hooks/useTokenRefresh';
import { profileService } from 'services/auth/authService';
import i18n from 'config/i18n';
import './App.css';
import Spinner from 'components/common/Spinner';
import { logger } from 'utils/logger';

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Enable automatic token refresh (refreshes 2 minutes before expiry)
  useTokenRefresh(isLoggedIn);

  useEffect(() => {

    const validateToken = async () => {
      // Skip validation for public routes
      const publicRoutes = ['/login', '/registration', '/', '/verify-otp', '/forgot-password', '/reset-password'];
      if (publicRoutes.includes(location.pathname)) {
        setIsLoggedIn(false);
        setIsLoading(false);
        return;
      }

      // Skip *full* validation if just logged in, but still hydrate subscription state
      // into Redux so the onboarding redirect can fire correctly.
      if (localStorage.getItem('justLoggedIn') === 'true') {
        setIsLoggedIn(true);
        localStorage.removeItem('justLoggedIn');

        const token = getAccessToken();
        if (token) {
          // Populate Redux's isExpired / planName / expiryDate / daysRemaining.
          // Without this, a fresh-login user always shows isExpired=false (initial state)
          // and skips the redirect to /onboarding.
          try {
            await validateTokenWithServer(token, dispatch);
          } catch (validateError) {
            logger.warn('Failed to validate token after login:', validateError);
          }
        }

        // Sync language from user's profile preference even when just logged in
        try {
          const profileResponse = await profileService.getProfile();
          // Backend sometimes returns the profile fields at the top level and
          // sometimes wrapped in a `data` envelope; keep both shapes accessible.
          const responseData = profileResponse.data as unknown as {
            data?: { preferred_language?: string; subscription_categories?: unknown };
            preferred_language?: string;
            subscription_categories?: unknown;
          };
          const preferredLanguage = responseData?.data?.preferred_language || responseData?.preferred_language;
          if (preferredLanguage && i18n.language !== preferredLanguage) {
            await i18n.changeLanguage(preferredLanguage);
          }
          const categories = responseData?.data?.subscription_categories || responseData?.subscription_categories;
          if (Array.isArray(categories)) {
            dispatch(setSubscriptionCategories(categories as string[]));
          }
        } catch (profileError) {
          logger.warn('Failed to sync language preference:', profileError);
        }

        setIsLoading(false);
        return;
      }

      try {
        let token = getAccessToken();

        // Cross-tab bootstrap: sessionStorage is per-tab, so a fresh tab (e.g.
        // user clicked an email link) has no access token even if the original
        // tab is logged in. The refresh-token COOKIE is cross-tab — use it to
        // silently mint a new access token before falling back to /login.
        if (!token) {
          try {
            const refreshed = await refreshAccessToken(dispatch);
            if (refreshed) token = refreshed;
          } catch {
            // Silently — we'll fall through to the /login redirect below
          }
        }

        if (token) {
          // First check if token is locally valid (not expired)
          // The token refresh mechanism will handle expired tokens
          const isValid = await checkFullTokenValidation(token, dispatch);

          setIsLoggedIn(isValid);
          if (isValid) {
            // Sync language from user's profile preference
            try {
              const profileResponse = await profileService.getProfile();
              const responseData = profileResponse.data as unknown as {
                data?: { preferred_language?: string; subscription_categories?: unknown };
                preferred_language?: string;
                subscription_categories?: unknown;
              };
              const preferredLanguage = responseData?.data?.preferred_language || responseData?.preferred_language;
              if (preferredLanguage && i18n.language !== preferredLanguage) {
                await i18n.changeLanguage(preferredLanguage);
              }
              const categories = responseData?.data?.subscription_categories || responseData?.subscription_categories;
              if (Array.isArray(categories)) {
                dispatch(setSubscriptionCategories(categories as string[]));
              }
            } catch {
              // Silently fail - language sync is not critical
            }
            // Note: Token refresh is handled by useTokenRefresh hook
          } else {
            // Preserve the intended URL so Login can redirect back after success
            navigate('/login', { state: { from: location.pathname + location.search } });
          }
        } else {
          setIsLoggedIn(false);
          navigate('/login', { state: { from: location.pathname + location.search } });
        }
      } catch {
        setIsLoggedIn(false);
        navigate('/login', { state: { from: location.pathname + location.search } });
        toast.error('Failed to validate session. Please log in again.');
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [dispatch, navigate, location.pathname, location.search]);

  useEffect(() => {
    const handleResize = () => {
      if (window.location.pathname === '/') {
        document.body.style.overflowY = 'scroll';
      } else {
        document.body.style.overflowY = window.innerWidth < 768 ? 'scroll' : 'hidden';
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      document.body.style.overflowY = '';
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isLoading) {
   return <Spinner size="large" text="Loading..." fullPage />;
  }

  // GoogleOAuthProvider is already mounted at the app root (src/index.tsx).
  // Re-nesting it here would cause a second GIS script load and duplicate side
  // effects, so App.tsx renders only its own children.
  return (
    <>
      <Router setIsLoggedIn={setIsLoggedIn} />
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} closeButton />
    </>
  );
};

export default App;